import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react'

import { useUnit } from 'effector-react'

import CloseIcon from '@/assets/icons/icon-close.svg'
import SearchIcon from '@/assets/icons/icon-search.svg'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { CheckedState } from '@/components/ui/form/form-checkbox'
import { TextField } from '@/components/ui/text-field.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { AI_LABEL_PREFIX } from '@/consts/vault.ts'
import { $isAiChattingEnabled } from '@/models/ai-features/ai-chatting.ts'
import {
  $mediaQueryParam,
  setMediaQuery,
} from '@/models/vault/medias/options.ts'
import { classNames } from '@/utils/helpers.ts'

interface Props {
  className?: string
}

export const VaultMediaSearchBox = ({ className }: Props) => {
  const mediaQueryParam = useUnit($mediaQueryParam)
  const isAiChattingFeatureEnabled = useUnit($isAiChattingEnabled)

  const checkboxId = useId()

  const [isAiSearchEnabled, setIsAiSearchEnabled] = useState(() =>
    mediaQueryParam.startsWith(AI_LABEL_PREFIX),
  )
  const [query, setQuery] = useState(() =>
    mediaQueryParam.startsWith(AI_LABEL_PREFIX)
      ? mediaQueryParam.slice(AI_LABEL_PREFIX.length).trimStart()
      : mediaQueryParam,
  )

  const buildSearchValue = useCallback((text: string, isAi: boolean) => {
    return isAi ? `${AI_LABEL_PREFIX} ${text}` : text
  }, [])

  const resetQuery = useCallback(() => {
    setQuery('')
    setMediaQuery(buildSearchValue('', isAiSearchEnabled))
  }, [buildSearchValue, isAiSearchEnabled])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newQuery = event.target.value

      setQuery(newQuery)
      setMediaQuery(buildSearchValue(newQuery, isAiSearchEnabled))
    },
    [buildSearchValue, isAiSearchEnabled],
  )

  const handleAiCheckboxChange = useCallback(
    (checked: CheckedState) => {
      const newIsAi = !!checked

      setIsAiSearchEnabled(newIsAi)
      setMediaQuery(buildSearchValue(query, newIsAi))
    },
    [buildSearchValue, query],
  )

  // Only sync FROM store TO component (external resets)
  useEffect(() => {
    const hasPrefix = mediaQueryParam.startsWith(AI_LABEL_PREFIX)
    const normalizedQuery = hasPrefix
      ? mediaQueryParam.slice(AI_LABEL_PREFIX.length).trimStart()
      : mediaQueryParam

    setIsAiSearchEnabled(hasPrefix)
    setQuery(normalizedQuery)
  }, [mediaQueryParam])

  return (
    <div className="flex items-center gap-3">
      {isAiChattingFeatureEnabled && (
        <Tooltip
          asChild
          content="Search only in AICH medias"
        >
          <label
            className="flex items-center gap-2 text-sm text-title cursor-pointer"
            htmlFor={checkboxId}
          >
            <Checkbox
              id={checkboxId}
              checked={isAiSearchEnabled}
              onCheckedChange={handleAiCheckboxChange}
            />
            AICH
          </label>
        </Tooltip>
      )}
      <TextField
        suffix={
          query ? (
            <CloseIcon
              onClick={resetQuery}
              className="h-4 w-4 cursor-pointer hover:opacity-70"
            />
          ) : null
        }
        prefix={
          <div className="flex items-center gap-1 text-secondary-400">
            <SearchIcon />
            {isAiSearchEnabled && (
              <span className="font-semibold text-primary-500">
                {AI_LABEL_PREFIX}
              </span>
            )}
          </div>
        }
        height={16}
        className={classNames('py-1', className)}
        placeholder="Search Vault"
        value={query}
        onChange={handleChange}
      />
    </div>
  )
}
